import { useEffect } from "react";
import { useLang } from "@/lib/i18n";

const SITE = "https://yatusabespr.com";
// The site's own default share image — the San Juan park photograph from the
// landing slideshow (src/data/content.js), cropped to the 1200×630 card size.
const DEFAULT_IMAGE =
  "https://media.base44.com/images/public/6ab7c499b9f1892aef65dca6/51443d4cd_image.png/v1/fill/w_1200,h_630,al_c,q_80,enc_jpg,quality_auto/51443d4cd_image.jpg";

// Every og:image must be an absolute https URL. A page may hand over a full
// URL or a site-relative path; an empty, broken or non-https value falls back
// to the default instead of producing an invalid tag.
function absoluteImage(candidate) {
  if (typeof candidate !== "string" || !candidate.trim()) return DEFAULT_IMAGE;
  try {
    const url = new URL(candidate.trim(), SITE);
    return url.protocol === "https:" ? url.href : DEFAULT_IMAGE;
  } catch {
    return DEFAULT_IMAGE;
  }
}

/**
 * @param {{ title?: string, titleEs?: string, description?: string, descriptionEs?: string, path?: string, image?: string, type?: string, schema?: object | object[], noindex?: boolean, langOverride?: string }} props
 */
export default function SEO({
  title,
  titleEs,
  description,
  descriptionEs,
  path = "",
  image,
  type = "website",
  schema,
  noindex = false,
  langOverride,
  standaloneBrand,
}) {
  const { lang } = useLang();
  const effectiveLang = langOverride || lang;
  useEffect(() => {
    const localizedTitle = effectiveLang === "es" ? (titleEs || title) : title;
    const localizedDescription = effectiveLang === "es" ? (descriptionEs || description) : description;
    const fullTitle = localizedTitle ? `${localizedTitle} | ${standaloneBrand || "Ya Tú Sabes PR"}` : (effectiveLang === "es" ? "Ya Tú Sabes PR — Guía local de Puerto Rico" : "Ya Tú Sabes PR — Puerto Rico's Local Guide");
    const desc = localizedDescription || (effectiveLang === "es" ? "Explora Puerto Rico más allá de lo obvio: negocios locales, comida, cultura, barrios, eventos y experiencias." : "Explore Puerto Rico beyond the obvious: local businesses, food, culture, neighborhoods, events and experiences.");

    document.title = fullTitle;
    document.documentElement.lang = effectiveLang === "es" ? "es-PR" : "en";

    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
    };

    setMeta('meta[name="description"]', { name: "description", content: desc });
    const robotsContent = noindex
      ? "noindex,nofollow"
      : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";
    setMeta('meta[name="robots"]', { name: "robots", content: robotsContent });
    setMeta('meta[name="googlebot"]', { name: "googlebot", content: robotsContent });
    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: standaloneBrand || "Ya Tú Sabes PR" });
    setMeta('meta[property="og:locale"]', { property: "og:locale", content: effectiveLang === "es" ? "es_PR" : "en_US" });
    setMeta('meta[property="og:locale:alternate"]', { property: "og:locale:alternate", content: effectiveLang === "es" ? "en_US" : "es_PR" });
    setMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    setMeta('meta[property="og:description"]', { property: "og:description", content: desc });
    setMeta('meta[property="og:type"]', { property: "og:type", content: type });
    setMeta('meta[property="og:url"]', { property: "og:url", content: new URL(path || "/", SITE).href });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: desc });

    const imageUrl = absoluteImage(image);
    setMeta('meta[property="og:image"]', { property: "og:image", content: imageUrl });
    setMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: standaloneBrand ? `${fullTitle} — fragrance boutique in Puerto Rico` : `${fullTitle} — Ya Tú Sabes PR, Puerto Rico local guide` });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: imageUrl });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", new URL(path || "/", SITE).href);

    document.head.querySelectorAll('script[data-yts-schema="true"]').forEach((node) => node.remove());
    const siteSchemas = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": `${SITE}/#organization`,
        name: "Ya Tú Sabes PR",
        url: SITE,
        logo: "https://media.base44.com/images/public/6ab7c499b9f1892aef65dca6/95f85bf60_logo.png",
        description: "A local guide to Puerto Rico's businesses, food, culture, neighborhoods, events and bookable experiences.",
        areaServed: { "@type": "Place", name: "Puerto Rico", addressCountry: "PR" },
        knowsLanguage: ["es-PR", "en"],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${SITE}/#website`,
        name: "Ya Tú Sabes PR",
        url: SITE,
        inLanguage: effectiveLang,
        publisher: { "@id": `${SITE}/#organization` },
      },
    ];
    {
      const schemas = [...(standaloneBrand ? [] : siteSchemas), ...(schema ? (Array.isArray(schema) ? schema : [schema]) : [])];
      schemas.forEach((item) => {
        if (!item) return;
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.ytsSchema = "true";
        script.textContent = JSON.stringify(item);
        document.head.appendChild(script);
      });
    }

    return () => {
      document.head.querySelectorAll('script[data-yts-schema="true"]').forEach((node) => node.remove());
    };
  }, [title, titleEs, description, descriptionEs, path, image, type, schema, noindex, effectiveLang, standaloneBrand]);

  return null;
}